import { supabase, ServiceResponse, handleServiceCall } from './supabase';

export interface Shipment {
    id: string;
    tracking_no: string;
    batch_id: string;
    weight: number;
    volume?: number;
    status: 'pending' | 'shipped' | 'received';
    created_at: string;
    length?: number;
    width?: number;
    height?: number;
    shipper_name?: string;
    parent_id?: string;
    package_tag?: 'standard' | 'merge_parent' | 'merged_child' | 'split_parent' | 'split_child';
    sender_at?: string;
    transit_at?: string;
    receiver_at?: string;
    transport_mode?: number; // 1: Land, 2: Sea, 3: Air
    item_category?: string;
    transit_weight?: number;
    transit_length?: number;
    transit_width?: number;
    transit_height?: number;
    receiver_weight?: number;
    receiver_length?: number;
    receiver_width?: number;
    receiver_height?: number;
    // 收货人信息（来自地址簿，打印到面单）
    receiver_name?: string;
    receiver_phone?: string;
    receiver_address?: string;
    receiver_city?: string;
}

export const ShipmentService = {
    /**
     * Internal helper to check if a batch is locked for mutations
     */
    async _checkBatchLock(batchId: string): Promise<void> {
        const { data: batch, error } = await supabase
            .from('batches')
            .select('status')
            .eq('id', batchId)
            .single();

        if (error) throw new Error('无法验证批次状态');

        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) return;

        const { data: profile } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', userData.user.id)
            .single();

        const role = profile?.role;
        let lockedStatuses: string[] = ['completed', 'cancelled'];

        if (role === 'sender') {
            lockedStatuses = ['completed', 'cancelled', 'sender_sealed', 'transit_processing', 'transit_sealed', 'receiver_processing', 'sealed', 'in_transit', 'inspected', 'received'];
        } else if (role === 'transit') {
            lockedStatuses = ['completed', 'cancelled', 'transit_sealed', 'receiver_processing', 'received'];
        } else if (role === 'receiver') {
            lockedStatuses = ['completed', 'cancelled'];
        }

        if (lockedStatuses.includes(batch.status)) {
            throw new Error(`该批次已处于“${batch.status}”状态，当前角色不可修改。`);
        }
    },

    /**
     * Create (Add) a Shipment to a Batch
     */
    async create(data: { tracking_no?: string; batch_id: string; weight: number; volume?: number; length?: number; width?: number; height?: number; shipper_name?: string; transport_mode?: number; item_category?: string }): Promise<ServiceResponse<Shipment>> {
        try {
            await this._checkBatchLock(data.batch_id);
            let finalTrackingNo = data.tracking_no;

            // Generate tracking_no if missing
            if (!finalTrackingNo) {
                // Fetch company_id of the batch
                const { data: batch, error: batchError } = await supabase.from('batches').select('sender_company_id').eq('id', data.batch_id).single();
                if (batchError || !batch) throw new Error('无法读取批次信息来生成单号');

                const { data: generatedNo, error: rpcError } = await supabase.rpc('generate_next_number', {
                    p_company_id: batch.sender_company_id,
                    p_type: 'tracking_no'
                });

                if (rpcError) throw new Error(`生成运单号失败: ${rpcError.message}`);
                finalTrackingNo = generatedNo;
            }

            return handleServiceCall(
                supabase.from('shipments')
                    .insert({ ...data, tracking_no: finalTrackingNo, status: 'pending' })
                    .select('*')
                    .single()
            );
        } catch (e: any) {
            return { data: null, error: e.message, success: false };
        }
    },

    /**
     * Bulk Create
     */
    async createMany(data: Partial<Shipment>[]): Promise<ServiceResponse<Shipment[]>> {
        return handleServiceCall(
            supabase.from('shipments')
                .insert(data)
                .select('*')
        );
    },

    /**
     * Get VALID Shipments for a Batch
     */
    async listByBatch(batchId: string, includeAll: boolean = false): Promise<ServiceResponse<Shipment[]>> {
        let query = supabase.from('shipments')
            .select('*')
            .eq('batch_id', batchId)
            .order('created_at', { ascending: false });

        if (!includeAll) {
            query = query.not('package_tag', 'in', '("merged_child","split_parent")');
        }

        return handleServiceCall(query);
    },

    async listRelations(batchId: string): Promise<ServiceResponse<any[]>> {
        const finalQuery = supabase.from('shipment_relations')
            .select('*, parent:shipments!parent_shipment_id!inner(batch_id)')
            .eq('parent.batch_id', batchId);
        return handleServiceCall(finalQuery);
    },

    async listAll(): Promise<ServiceResponse<Shipment[]>> {
        return handleServiceCall(
            supabase.from('shipments').select('*').order('created_at', { ascending: false })
        );
    },

    async findByTracking(trackingNo: string): Promise<ServiceResponse<Shipment>> {
        return handleServiceCall(
            supabase.from('shipments').select('*').eq('tracking_no', trackingNo).single()
        );
    },

    async moveToBatch(shipmentId: string, newBatchId: string): Promise<ServiceResponse<Shipment>> {
        return handleServiceCall(
            supabase.from('shipments').update({ batch_id: newBatchId }).eq('id', shipmentId).select().single()
        );
    },

    /**
     * Update Weight or Status
     */
    async update(id: string, updates: Partial<Shipment>): Promise<ServiceResponse<Shipment>> {
        try {
            let batchId = updates.batch_id;
            if (!batchId) {
                const { data } = await supabase.from('shipments').select('batch_id').eq('id', id).single();
                batchId = data?.batch_id;
            }
            if (batchId) await this._checkBatchLock(batchId);

            return handleServiceCall(
                supabase.from('shipments').update(updates).eq('id', id).select().single()
            );
        } catch (e: any) {
            return { data: null, error: e.message, success: false };
        }
    },

    /**
     * Merge Multiple Shipments into One
     */
    async mergeShipments(data: {
        parent_tracking_no: string;
        child_ids: string[];
        batch_id: string;
        total_weight: number;
        volume?: number;
        role?: 'transit' | 'receiver';
    }): Promise<ServiceResponse<Shipment>> {
        const { data: { user } } = await supabase.auth.getUser();
        const stage = data.role || 'transit';

        try {
            await this._checkBatchLock(data.batch_id);
            const parentResp = await handleServiceCall<Shipment>(
                supabase.from('shipments').insert({
                    tracking_no: data.parent_tracking_no,
                    batch_id: data.batch_id,
                    weight: data.total_weight,
                    volume: data.volume,
                    status: stage === 'receiver' ? 'received' : 'pending',
                    package_tag: 'merge_parent'
                }).select().single()
            );

            if (!parentResp.success) return parentResp;
            const parent = parentResp.data!;

            await supabase.from('inspections').insert({
                batch_id: data.batch_id,
                inspector_id: user?.id,
                result: 'passed',
                notes: stage === 'receiver'
                    ? `ReceiverItemCheck ShipmentID:${parent.id} (Auto-Merge Parent)`
                    : `WeighCheck ShipmentID:${parent.id} (Auto-Merge Parent)`,
                transit_weight: stage === 'transit' ? data.total_weight : null,
                check_weight: stage === 'receiver' ? data.total_weight : null,
                photos: []
            });

            const relations = data.child_ids.map(childId => ({
                parent_shipment_id: parent.id,
                child_shipment_id: childId,
                type: 'merge'
            }));

            const relResp = await handleServiceCall<any>(
                supabase.from('shipment_relations').insert(relations)
            );

            if (!relResp.success) return { data: null, error: relResp.error, success: false };

            await supabase.from('shipments').update({ status: 'shipped', package_tag: 'merged_child' }).in('id', data.child_ids);

            return { data: parent, error: null, success: true };
        } catch (e: any) {
            return { data: null, error: e.message, success: false };
        }
    },

    /**
     * Split One Shipment into Multiple
     */
    async splitShipment(data: {
        parent_id: string;
        children: Array<{ tracking_no: string; weight: number; volume?: number }>;
        batch_id: string;
        role?: 'transit' | 'receiver';
    }): Promise<ServiceResponse<Shipment[]>> {
        const { data: { user } } = await supabase.auth.getUser();
        const stage = data.role || 'transit';

        try {
            await this._checkBatchLock(data.batch_id);

            const childrenToInsert = data.children.map(child => ({
                tracking_no: child.tracking_no,
                batch_id: data.batch_id,
                weight: child.weight,
                volume: child.volume,
                status: stage === 'receiver' ? 'received' : 'pending',
                package_tag: 'split_child'
            }));

            const childResp = await handleServiceCall<Shipment[]>(
                supabase.from('shipments').insert(childrenToInsert).select()
            );

            if (!childResp.success) return childResp;
            const children = childResp.data!;

            const autoInspections = children.map(child => ({
                batch_id: data.batch_id,
                inspector_id: user?.id,
                result: 'passed',
                notes: stage === 'receiver'
                    ? `ReceiverItemCheck ShipmentID:${child.id} (Auto-Split Sub)`
                    : `WeighCheck ShipmentID:${child.id} (Auto-Split Sub)`,
                transit_weight: stage === 'transit' ? child.weight : null,
                check_weight: stage === 'receiver' ? child.weight : null,
                photos: []
            }));

            await supabase.from('inspections').insert(autoInspections);

            const relations = children.map(child => ({
                parent_shipment_id: data.parent_id,
                child_shipment_id: child.id,
                type: 'split'
            }));

            const relResp = await handleServiceCall<any>(
                supabase.from('shipment_relations').insert(relations)
            );

            if (!relResp.success) return { data: null, error: relResp.error, success: false };

            await supabase.from('shipments').update({ status: 'shipped', package_tag: 'split_parent' }).eq('id', data.parent_id);

            return { data: children, error: null, success: true };
        } catch (e: any) {
            return { data: null, error: e.message, success: false };
        }
    },

    async remove(id: string): Promise<ServiceResponse<null>> {
        try {
            const { data } = await supabase.from('shipments').select('batch_id').eq('id', id).single();
            if (data?.batch_id) await this._checkBatchLock(data.batch_id);

            const { error } = await supabase.from('shipments').delete().eq('id', id);
            if (error) return { data: null, error: error.message, success: false };
            return { data: null, error: null, success: true };
        } catch (e: any) {
            return { data: null, error: e.message, success: false };
        }
    },

    async removeInBatch(batchId: string, trackingNos: string[]): Promise<ServiceResponse<null>> {
        try {
            await this._checkBatchLock(batchId);
            const { error } = await supabase.from('shipments')
                .delete()
                .eq('batch_id', batchId)
                .in('tracking_no', trackingNos);
            if (error) return { data: null, error: error.message, success: false };
            return { data: null, error: null, success: true };
        } catch (e: any) {
            return { data: null, error: e.message, success: false };
        }
    }
};
